import { useState, useRef } from "react"
import { useTranslations } from "next-intl"
import {
	User, Mail, Lock, Loader2, AlertCircle, CheckCircle2,
	Building2, Upload, Trash2, Image as ImageIcon, Save,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuthStore } from "@/stores/authStore"
import { api } from "@/lib/apiService"

const BRAND_COLOR = "#2D5C40"

interface ApiResponse<T> {
	success: boolean
	data: T
	user?: { id: string; email: string; name: string; role?: string; company_id?: string }
	token?: string
}

interface CompanyData {
	id: string
	name: string
	logo_url?: string
	is_platform_admin?: boolean
}

export default function SettingsPage() {
	const t = useTranslations("settings")
	const { user, company, updateUser, setCompany } = useAuthStore()

	// Profile state
	const [name, setName] = useState(user?.name || "")
	const [savingName, setSavingName] = useState(false)
	const [nameError, setNameError] = useState<string | null>(null)
	const [nameSuccess, setNameSuccess] = useState(false)

	// Password state
	const [currentPassword, setCurrentPassword] = useState("")
	const [newPassword, setNewPassword] = useState("")
	const [confirmPassword, setConfirmPassword] = useState("")
	const [savingPassword, setSavingPassword] = useState(false)
	const [passwordError, setPasswordError] = useState<string | null>(null)
	const [passwordSuccess, setPasswordSuccess] = useState(false)

	// Company name state
	const [companyName, setCompanyName] = useState(company?.name || "")
	const [savingCompanyName, setSavingCompanyName] = useState(false)
	const [companyNameError, setCompanyNameError] = useState<string | null>(null)
	const [companyNameSuccess, setCompanyNameSuccess] = useState(false)

	// Logo state
	const [uploadingLogo, setUploadingLogo] = useState(false)
	const [deletingLogo, setDeletingLogo] = useState(false)
	const [logoError, setLogoError] = useState<string | null>(null)
	const [logoSuccess, setLogoSuccess] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	// ── Profile handlers ─────────────────────────────────────────────

	const handleSaveName = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim()) {
			setNameError(t("nameRequired"))
			return
		}

		setSavingName(true)
		setNameError(null)
		setNameSuccess(false)

		try {
			const res = await api.put<ApiResponse<null>>("/auth/profile", { name: name.trim() })
			if (res.user && res.token) {
				updateUser(res.user, res.token)
			}
			setNameSuccess(true)
			setTimeout(() => setNameSuccess(false), 3000)
		} catch {
			setNameError(t("nameRequired"))
		} finally {
			setSavingName(false)
		}
	}

	// ── Company name handler ─────────────────────────────────────────

	const handleSaveCompanyName = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!companyName.trim()) {
			setCompanyNameError(t("nameRequired"))
			return
		}

		setSavingCompanyName(true)
		setCompanyNameError(null)
		setCompanyNameSuccess(false)

		try {
			const res = await api.put<ApiResponse<CompanyData>>("/companies/me/name", { name: companyName.trim() })
			if (res.success && res.data) {
				setCompany(res.data)
			}
			setCompanyNameSuccess(true)
			setTimeout(() => setCompanyNameSuccess(false), 3000)
		} catch {
			setCompanyNameError(t("nameRequired"))
		} finally {
			setSavingCompanyName(false)
		}
	}

	// ── Password handlers ────────────────────────────────────────────

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault()
		setPasswordError(null)
		setPasswordSuccess(false)

		if (!currentPassword) {
			setPasswordError(t("currentPasswordRequired"))
			return
		}
		if (!newPassword) {
			setPasswordError(t("newPasswordRequired"))
			return
		}
		if (newPassword.length < 6) {
			setPasswordError(t("passwordTooShort"))
			return
		}
		if (newPassword !== confirmPassword) {
			setPasswordError(t("passwordsDoNotMatch"))
			return
		}

		setSavingPassword(true)

		try {
			await api.put("/auth/change-password", { currentPassword, newPassword })
			setCurrentPassword("")
			setNewPassword("")
			setConfirmPassword("")
			setPasswordSuccess(true)
			setTimeout(() => setPasswordSuccess(false), 3000)
		} catch {
			setPasswordError(t("currentPasswordRequired"))
		} finally {
			setSavingPassword(false)
		}
	}

	// ── Logo handlers ────────────────────────────────────────────────

	const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file || !company?.id) return

		const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"]
		if (!allowedTypes.includes(file.type)) {
			setLogoError(t("logoInvalidType"))
			return
		}
		if (file.size > 5 * 1024 * 1024) {
			setLogoError(t("logoTooLarge"))
			return
		}

		setUploadingLogo(true)
		setLogoError(null)
		setLogoSuccess(null)

		try {
			const formData = new FormData()
			formData.append("logo", file)

			const res = await api.upload<ApiResponse<CompanyData>>("/companies/me/logo", formData)
			if (res.success && res.data) {
				setCompany(res.data)
			}
			if (fileInputRef.current) fileInputRef.current.value = ""
			setLogoSuccess(t("logoUpdated"))
			setTimeout(() => setLogoSuccess(null), 3000)
		} catch {
			setLogoError(t("logoUploadError"))
		} finally {
			setUploadingLogo(false)
		}
	}

	const handleDeleteLogo = async () => {
		if (!company?.id || !company?.logo_url) return
		if (!confirm(t("logoDeleteConfirm"))) return

		setDeletingLogo(true)
		setLogoError(null)
		setLogoSuccess(null)

		try {
			const res = await api.delete<ApiResponse<CompanyData>>("/companies/me/logo")
			if (res.success && res.data) {
				setCompany(res.data)
			}
			setLogoSuccess(t("logoDeleted"))
			setTimeout(() => setLogoSuccess(null), 3000)
		} catch {
			setLogoError(t("logoDeleteError"))
		} finally {
			setDeletingLogo(false)
		}
	}

	if (!user) {
		return (
			<DashboardLayout>
				<div className="p-6">
					<div className="space-y-8">
						{/* Header skeleton */}
						<div className="space-y-2">
							<Skeleton className="h-8 w-48" />
							<Skeleton className="h-4 w-80" />
						</div>
						{/* Company card skeleton */}
						<Card>
							<CardHeader>
								<div className="flex items-center gap-2">
									<Skeleton className="h-5 w-5 rounded" />
									<Skeleton className="h-5 w-32" />
								</div>
								<Skeleton className="h-4 w-64" />
							</CardHeader>
							<CardContent>
								<div className="grid gap-6 md:grid-cols-2">
									<div className="space-y-3">
										<Skeleton className="h-4 w-40" />
										<div className="flex items-start gap-4">
											<Skeleton className="h-20 w-20 rounded-lg" />
											<div className="space-y-2">
												<Skeleton className="h-3 w-48" />
												<Skeleton className="h-8 w-28 rounded-md" />
											</div>
										</div>
									</div>
									<div className="space-y-3">
										<Skeleton className="h-4 w-44" />
										<Skeleton className="h-10 w-full rounded-md" />
									</div>
								</div>
							</CardContent>
						</Card>
						{/* Profile + Password cards skeleton */}
						<div className="grid gap-6 md:grid-cols-2">
							<Card>
								<CardHeader>
									<div className="flex items-center gap-2">
										<Skeleton className="h-5 w-5 rounded" />
										<Skeleton className="h-5 w-20" />
									</div>
									<Skeleton className="h-4 w-56" />
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="space-y-2">
										<Skeleton className="h-4 w-36" />
										<Skeleton className="h-10 w-full rounded-md" />
										<Skeleton className="h-3 w-52" />
									</div>
									<div className="space-y-2">
										<Skeleton className="h-4 w-20" />
										<Skeleton className="h-10 w-full rounded-md" />
									</div>
									<Skeleton className="h-10 w-36 rounded-md" />
								</CardContent>
							</Card>
							<Card>
								<CardHeader>
									<div className="flex items-center gap-2">
										<Skeleton className="h-5 w-5 rounded" />
										<Skeleton className="h-5 w-40" />
									</div>
									<Skeleton className="h-4 w-52" />
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="space-y-2">
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-10 w-full rounded-md" />
									</div>
									<div className="space-y-2">
										<Skeleton className="h-4 w-36" />
										<Skeleton className="h-10 w-full rounded-md" />
									</div>
									<div className="space-y-2">
										<Skeleton className="h-4 w-40" />
										<Skeleton className="h-10 w-full rounded-md" />
									</div>
									<Skeleton className="h-10 w-44 rounded-md" />
								</CardContent>
							</Card>
						</div>
					</div>
				</div>
			</DashboardLayout>
		)
	}

	return (
		<DashboardLayout>
			<div className="p-6">
				<div className="space-y-8">
					{/* Header */}
					<div className="space-y-1">
						<h1 className="text-2xl font-bold tracking-tight" style={{ color: BRAND_COLOR }}>
							{t("title")}
						</h1>
						<p className="text-muted-foreground">{t("subtitle")}</p>
					</div>

					{/* Company Card — full width */}
					{company && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Building2 className="h-5 w-5" style={{ color: BRAND_COLOR }} />
									{t("company")}
								</CardTitle>
								<CardDescription>{t("companyDescription")}</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="grid gap-6 md:grid-cols-2">
									{/* Logo section */}
									<div className="space-y-3">
										<Label>{t("companyLogo")}</Label>
										<div className="flex items-start gap-4">
											<div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
												{company.logo_url ? (
													<img
														src={company.logo_url}
														alt={company.name}
														className="h-full w-full object-contain p-1"
													/>
												) : (
													<ImageIcon className="h-8 w-8 text-muted-foreground" />
												)}
											</div>
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground">
													{t("logoDescription")}
												</p>
												<div className="flex gap-2">
													<input
														ref={fileInputRef}
														type="file"
														accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
														className="hidden"
														onChange={handleLogoUpload}
													/>
													<Button
														size="sm"
														variant="outline"
														onClick={() => fileInputRef.current?.click()}
														disabled={uploadingLogo}
													>
														{uploadingLogo ? (
															<Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
														) : (
															<Upload className="mr-2 h-3.5 w-3.5" />
														)}
														{company.logo_url ? t("changeLogo") : t("uploadLogo")}
													</Button>
													{company.logo_url && (
														<Button
															size="sm"
															variant="ghost"
															className="text-destructive hover:text-destructive"
															onClick={handleDeleteLogo}
															disabled={deletingLogo}
														>
															{deletingLogo ? (
																<Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
															) : (
																<Trash2 className="mr-2 h-3.5 w-3.5" />
															)}
															{t("removeLogo")}
														</Button>
													)}
												</div>
												{logoError && (
													<p className="text-sm text-destructive flex items-center gap-1">
														<AlertCircle className="h-3 w-3" />
														{logoError}
													</p>
												)}
												{logoSuccess && (
													<p className="text-sm text-green-600 flex items-center gap-1">
														<CheckCircle2 className="h-3 w-3" />
														{logoSuccess}
													</p>
												)}
											</div>
										</div>
									</div>

									{/* Company name (editable) */}
									<form onSubmit={handleSaveCompanyName} className="space-y-3">
										<div className="space-y-2">
											<Label>{t("companyName")}</Label>
											<Input
												type="text"
												value={companyName}
												onChange={(e) => {
													setCompanyName(e.target.value)
													setCompanyNameError(null)
													setCompanyNameSuccess(false)
												}}
											/>
										</div>
										{companyNameError && (
											<p className="text-sm text-destructive flex items-center gap-1">
												<AlertCircle className="h-3 w-3" />
												{companyNameError}
											</p>
										)}
										{companyNameSuccess && (
											<p className="text-sm text-green-600 flex items-center gap-1">
												<CheckCircle2 className="h-3 w-3" />
												{t("saved")}
											</p>
										)}
										<Button type="submit" disabled={savingCompanyName || !companyName.trim()}>
											{savingCompanyName ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													{t("saveName")}
												</>
											) : (
												<>
													<Save className="mr-2 h-4 w-4" />
													{t("saveName")}
												</>
											)}
										</Button>
									</form>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Profile + Password — 2-column grid */}
					<div className="grid gap-6 md:grid-cols-2">
						{/* Profile Card */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<User className="h-5 w-5" style={{ color: BRAND_COLOR }} />
									{t("profile")}
								</CardTitle>
								<CardDescription>{t("profileDescription")}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Email (read-only) */}
								<div className="space-y-2">
									<Label className="flex items-center gap-2">
										<Mail className="h-4 w-4" />
										{t("email")}
									</Label>
									<Input
										type="email"
										value={user.email}
										disabled
										className="bg-muted"
									/>
									<p className="text-xs text-muted-foreground">{t("emailCannotChange")}</p>
								</div>

								{/* Name form */}
								<form onSubmit={handleSaveName} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="name">{t("name")}</Label>
										<Input
											id="name"
											type="text"
											value={name}
											onChange={(e) => {
												setName(e.target.value)
												setNameError(null)
												setNameSuccess(false)
											}}
											placeholder={t("namePlaceholder")}
										/>
									</div>
									{nameError && (
										<p className="text-sm text-destructive flex items-center gap-1">
											<AlertCircle className="h-3 w-3" />
											{nameError}
										</p>
									)}
									{nameSuccess && (
										<p className="text-sm text-green-600 flex items-center gap-1">
											<CheckCircle2 className="h-3 w-3" />
											{t("profileUpdated")}
										</p>
									)}
									<Button type="submit" disabled={savingName || !name.trim()}>
										{savingName ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												{t("saveName")}
											</>
										) : (
											<>
												<Save className="mr-2 h-4 w-4" />
												{t("saveName")}
											</>
										)}
									</Button>
								</form>
							</CardContent>
						</Card>

						{/* Password Card */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Lock className="h-5 w-5" style={{ color: BRAND_COLOR }} />
									{t("changePassword")}
								</CardTitle>
								<CardDescription>{t("changePasswordDescription")}</CardDescription>
							</CardHeader>
							<CardContent>
								<form onSubmit={handleChangePassword} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="currentPassword">{t("currentPassword")}</Label>
										<Input
											id="currentPassword"
											type="password"
											value={currentPassword}
											onChange={(e) => {
												setCurrentPassword(e.target.value)
												setPasswordError(null)
												setPasswordSuccess(false)
											}}
											placeholder={t("currentPasswordPlaceholder")}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="newPassword">{t("newPassword")}</Label>
										<Input
											id="newPassword"
											type="password"
											value={newPassword}
											onChange={(e) => {
												setNewPassword(e.target.value)
												setPasswordError(null)
												setPasswordSuccess(false)
											}}
											placeholder={t("newPasswordPlaceholder")}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="confirmPassword">{t("confirmPassword")}</Label>
										<Input
											id="confirmPassword"
											type="password"
											value={confirmPassword}
											onChange={(e) => {
												setConfirmPassword(e.target.value)
												setPasswordError(null)
												setPasswordSuccess(false)
											}}
											placeholder={t("confirmPasswordPlaceholder")}
										/>
									</div>
									{passwordError && (
										<p className="text-sm text-destructive flex items-center gap-1">
											<AlertCircle className="h-3 w-3" />
											{passwordError}
										</p>
									)}
									{passwordSuccess && (
										<p className="text-sm text-green-600 flex items-center gap-1">
											<CheckCircle2 className="h-3 w-3" />
											{t("passwordChanged")}
										</p>
									)}
									<Button
										type="submit"
										disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
									>
										{savingPassword ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												{t("updatePassword")}
											</>
										) : (
											<>
												<Lock className="mr-2 h-4 w-4" />
												{t("updatePassword")}
											</>
										)}
									</Button>
								</form>
							</CardContent>
						</Card>
					</div>

				</div>
			</div>
		</DashboardLayout>
	)
}
